import { reactive } from 'vue'

// Real weather via Open-Meteo (free, keyless, CORS-enabled):
// - forecast API: current conditions + 7-day daily forecast
// - archive API (ERA5): last-90-days rain + growing degree days, and the same
//   calendar window averaged over the previous 10 years for the delta
// Cached per ~5 km cell; consumers get null while the first fetch is in flight.

export interface DayForecast {
  date: string
  icon: 'sun' | 'cloud' | 'rain'
  tempC: number
  rainMm: number
}

export interface AgroWeather {
  forecast: DayForecast[]
  seasonStart: string
  seasonRainMm: number
  seasonRainDeltaPct: number
  gdd: number
  sprayWindow: string
}

export interface WeatherStats {
  weather: { tempC: number; humidityPct: number; windMs: number }
  agro: AgroWeather
}

const DAY = 86_400_000
const SEASON_DAYS = 90
const ARCHIVE_LAG_DAYS = 6 // ERA5 publishes with a few days' delay
const AVG_YEARS = 10
const GDD_BASE_C = 10
const RETRY_MS = 5 * 60_000

const cache = reactive(new Map<string, WeatherStats>())
const inflight = new Set<string>()
const failedAt = new Map<string, number>()

export function getWeather(lat: number, lon: number): WeatherStats | null {
  const key = `${lat.toFixed(2)},${lon.toFixed(2)}`
  const hit = cache.get(key)
  if (!hit && !inflight.has(key) && Date.now() - (failedAt.get(key) ?? 0) > RETRY_MS) {
    void load(key, lat, lon)
  }
  return hit ?? null
}

async function load(key: string, lat: number, lon: number) {
  inflight.add(key)
  try {
    const [now, season] = await Promise.all([fetchForecast(lat, lon), fetchSeason(lat, lon)])
    cache.set(key, {
      weather: now.weather,
      agro: {
        ...season,
        forecast: now.forecast,
        sprayWindow: sprayWindow(now.forecast, now.dailyWind),
      },
    })
  } catch {
    failedAt.set(key, Date.now())
  } finally {
    inflight.delete(key)
  }
}

function iconFor(wmoCode: number): DayForecast['icon'] {
  if (wmoCode <= 1) return 'sun'
  if (wmoCode <= 48) return 'cloud'
  return 'rain'
}

async function fetchForecast(lat: number, lon: number) {
  const qs = new URLSearchParams({
    latitude: String(lat),
    longitude: String(lon),
    current: 'temperature_2m,relative_humidity_2m,wind_speed_10m',
    daily: 'weather_code,temperature_2m_max,precipitation_sum,wind_speed_10m_max',
    wind_speed_unit: 'ms',
    timezone: 'auto',
    forecast_days: '7',
  })
  const r = await fetch(`https://api.open-meteo.com/v1/forecast?${qs}`)
  if (!r.ok) throw new Error(`forecast ${r.status}`)
  const d = await r.json()
  const forecast: DayForecast[] = d.daily.time.map((date: string, i: number) => ({
    date,
    icon: iconFor(d.daily.weather_code[i] ?? 0),
    tempC: d.daily.temperature_2m_max[i] ?? 0,
    rainMm: Math.round(d.daily.precipitation_sum[i] ?? 0),
  }))
  return {
    weather: {
      tempC: d.current.temperature_2m as number,
      humidityPct: d.current.relative_humidity_2m as number,
      windMs: d.current.wind_speed_10m as number,
    },
    forecast,
    dailyWind: (d.daily.wind_speed_10m_max ?? []) as number[],
  }
}

function sprayWindow(forecast: DayForecast[], dailyWind: number[]): string {
  const i = forecast.findIndex((day, idx) => day.rainMm < 1 && (dailyWind[idx] ?? 0) < 8)
  if (i < 0) return 'No good window this week'
  const weekday = new Date(forecast[i].date).toLocaleDateString('en-US', { weekday: 'short' })
  return `${weekday} 06:00–11:00`
}

async function fetchSeason(lat: number, lon: number) {
  const end = new Date(Date.now() - ARCHIVE_LAG_DAYS * DAY)
  const seasonStart = new Date(end.getTime() - SEASON_DAYS * DAY)
  const historyStart = new Date(seasonStart.getTime())
  historyStart.setFullYear(historyStart.getFullYear() - AVG_YEARS)
  const iso = (d: Date) => d.toISOString().slice(0, 10)
  const qs = new URLSearchParams({
    latitude: String(lat),
    longitude: String(lon),
    start_date: iso(historyStart),
    end_date: iso(end),
    daily: 'precipitation_sum,temperature_2m_max,temperature_2m_min',
    timezone: 'auto',
  })
  const r = await fetch(`https://archive-api.open-meteo.com/v1/archive?${qs}`)
  if (!r.ok) throw new Error(`archive ${r.status}`)
  const d = await r.json()
  const time: string[] = d.daily.time
  const rain: (number | null)[] = d.daily.precipitation_sum
  const tmax: (number | null)[] = d.daily.temperature_2m_max
  const tmin: (number | null)[] = d.daily.temperature_2m_min

  let seasonRain = 0
  let gdd = 0
  const priorRain: number[] = Array(AVG_YEARS).fill(0)
  const startMs = seasonStart.getTime()
  const endMs = end.getTime()
  for (let i = 0; i < time.length; i++) {
    const t = Date.parse(time[i])
    if (t >= startMs) {
      seasonRain += rain[i] ?? 0
      gdd += Math.max(0, ((tmax[i] ?? 0) + (tmin[i] ?? 0)) / 2 - GDD_BASE_C)
    } else {
      // does this day fall in the same 90-day window of a prior year?
      for (let y = 1; y <= AVG_YEARS; y++) {
        const shifted = new Date(t)
        shifted.setFullYear(shifted.getFullYear() + y)
        const s = shifted.getTime()
        if (s >= startMs && s <= endMs) {
          priorRain[y - 1] += rain[i] ?? 0
          break
        }
      }
    }
  }
  const priorAvg = priorRain.reduce((a, b) => a + b, 0) / AVG_YEARS || 1
  return {
    seasonStart: seasonStart.toISOString(),
    seasonRainMm: Math.round(seasonRain),
    seasonRainDeltaPct: Math.round(((seasonRain - priorAvg) / priorAvg) * 100),
    gdd: Math.round(gdd),
  }
}
